import type { FeaturedUnit, UnitGroup } from "@/types";

/**
 * Legacy CMS sort token. Featured units now always pin to the top (the "Featured" sort
 * option was removed), but a stored sort string may still contain this token — it is not a
 * valid Storage Essentials sort property, so it is stripped before the API call.
 */
export const FEATURED_SORT_TOKEN = "featured";

/** Compare a configured featured groupId against a loaded unit group. */
const matchesGroup = (unitGroup: UnitGroup, groupId: FeaturedUnit["groupId"]) =>
    String(unitGroup.representative_unit?.id) === String(groupId);

/** Returns the featured config for a unit group, or null if it is not featured. */
export const getFeaturedConfig = (
    unitGroup: UnitGroup,
    featuredUnits: FeaturedUnit[] | undefined,
): FeaturedUnit | null => {
    if (!featuredUnits?.length) return null;
    return (
        featuredUnits.find((featured) => matchesGroup(unitGroup, featured.groupId)) ??
        null
    );
};

/** Whether a unit group is configured as featured. */
export const isFeatured = (
    unitGroup: UnitGroup,
    featuredUnits: FeaturedUnit[] | undefined,
): boolean => getFeaturedConfig(unitGroup, featuredUnits) !== null;

/**
 * Stable reorder placing featured groups first while preserving the relative
 * order of both featured and non-featured groups.
 */
export const pinFeaturedToTop = (
    units: UnitGroup[],
    featuredUnits: FeaturedUnit[] | undefined,
): UnitGroup[] => {
    if (!featuredUnits?.length) return units;
    const featured: UnitGroup[] = [];
    const rest: UnitGroup[] = [];
    for (const unitGroup of units) {
        (isFeatured(unitGroup, featuredUnits) ? featured : rest).push(unitGroup);
    }
    return [...featured, ...rest];
};
